import { useEffect, useState } from 'react';
import {
  X,
  CheckSquare,
  UserPlus,
  ArrowRight,
  Tag,
  Loader2,
  AlertTriangle,
  Merge,
  Trash2,
} from 'lucide-react';
import { TicketTagInput } from './TicketTagInput';
import { tagService, type TagSuggestion } from '@services/tag.service';

interface BulkAction {
  id: string;
  label: string;
  icon: React.ElementType;
  variant?: 'default' | 'danger';
  options?: { value: string; label: string }[];
}

const BULK_ACTIONS: BulkAction[] = [
  {
    id: 'status',
    label: 'Change Status',
    icon: ArrowRight,
    options: [
      { value: 'new', label: 'New' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'pending', label: 'Pending' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'closed', label: 'Closed' },
    ],
  },
  {
    id: 'priority',
    label: 'Change Priority',
    icon: AlertTriangle,
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' },
    ],
  },
  {
    id: 'assign',
    label: 'Assign',
    icon: UserPlus,
    options: [
      { value: 'unassign', label: 'Unassign' },
      // Agent options would be populated dynamically
    ],
  },
  {
    id: 'tags',
    label: 'Add Tags',
    icon: Tag,
  },
  {
    id: 'merge',
    label: 'Merge',
    icon: Merge,
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: Trash2,
    variant: 'danger',
  },
];

interface BulkActionsBarProps {
  selectedCount: number;
  /** Rows currently loaded — what "select all" actually selects. */
  totalCount: number;
  /**
   * Rows matching the filters on the SERVER. Shown so the bar never implies the
   * loaded page is the whole result set; the select-all affordance still says
   * "on this page", because a bulk close that silently reached rows the agent
   * never saw is a different — and destructive — feature.
   */
  matchingTotal?: number;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onAction: (actionId: string, value?: string | string[]) => Promise<void>;
  isProcessing?: boolean;
  /** Show the Assign → Unassign action (admins and supervisors only). Defaults to true. */
  canUnassign?: boolean;
}

export function BulkActionsBar({
  selectedCount,
  totalCount,
  matchingTotal,
  onSelectAll,
  onClearSelection,
  onAction,
  isProcessing = false,
  canUnassign = true,
}: BulkActionsBarProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<TagSuggestion[]>([]);

  // Load existing org tags whenever the tag popover opens, so the agent can
  // see and click already-present tags instead of retyping them.
  useEffect(() => {
    if (activeDropdown !== 'tags') return;
    let cancelled = false;
    tagService
      .suggestions(undefined, 50)
      .then((list) => {
        if (!cancelled) setTagSuggestions(list);
      })
      .catch(() => {
        // suggestions are non-critical — the free-text input still works
      });
    return () => {
      cancelled = true;
    };
  }, [activeDropdown]);

  const addDraftTag = (name: string) => {
    setTagDraft((prev) => (prev.includes(name) ? prev : [...prev, name]));
  };

  const closeTagPopover = () => {
    setActiveDropdown(null);
    setTagDraft([]);
  };

  const handleActionClick = async (action: BulkAction) => {

    if (action.id === 'tags') {
      // Toggle the tag picker popover (no preset options — agent types/picks tags)
      if (activeDropdown === 'tags') {
        closeTagPopover();
      } else {
        setActiveDropdown('tags');
      }
      return;
    }

    if (action.options) {
      setActiveDropdown(activeDropdown === action.id ? null : action.id);
    } else {
      await onAction(action.id);
    }
  };

  const handleOptionSelect = async (actionId: string, value: string) => {
    setActiveDropdown(null);
    await onAction(actionId, value);
  };

  const handleApplyTags = async () => {
    if (tagDraft.length === 0) return;
    const tags = tagDraft;
    setActiveDropdown(null);
    setTagDraft([]);
    await onAction('tags', tags);
  };

  if (selectedCount === 0) return null;

  return (
    <>
      {/* A grouped surface INSET in the register card — wash and border, no
          shadow. A floating bar would be a fourth elevation layer on a page
          that already has canvas, card and popover. */}
      <div className="m-[14px] mb-0 flex flex-wrap items-center gap-3 rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-selected)] px-[14px] py-[10px]">
          {/* Selection Info */}
          <div className="flex items-center gap-3">
            <CheckSquare className="h-4 w-4 text-[var(--co-brass-ink)]" />
            <span className="co-mono text-[10px] font-semibold uppercase tracking-[.14em] text-[var(--co-brass-ink)]">
              {selectedCount} selected
            </span>
            {selectedCount < totalCount && (
              <button
                onClick={onSelectAll}
                className="co-mono text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-3)] hover:text-[var(--co-brass-ink)]"
              >
                Select all {totalCount} on this page
              </button>
            )}
            {matchingTotal !== undefined && matchingTotal > totalCount && (
              <span className="co-mono text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-4)]">
                {matchingTotal} match the filters
              </span>
            )}
          </div>

          <div className="h-[18px] w-px flex-none bg-[var(--co-line-strong)]" />

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {BULK_ACTIONS
              // Merge needs at least two tickets — only show it when 2+ selected.
              .filter((action) => action.id !== 'merge' || selectedCount > 1)
              // Its only option is Unassign, which is for admins and supervisors.
              .filter((action) => action.id !== 'assign' || canUnassign)
              .map((action) => (
              <div key={action.id} className="relative">
                <button
                  onClick={() => handleActionClick(action)}
                  disabled={isProcessing}
                  className={`
                    flex items-center gap-2 rounded-md px-[9px] py-[5px] text-[12.5px] transition-colors
                    ${action.variant === 'danger'
                      ? 'text-[var(--co-burgundy)] hover:bg-[var(--co-burgundy-wash)]'
                      : 'text-[var(--co-ink-2)] hover:text-[var(--co-brass-ink)] hover:bg-[var(--co-brass-wash)]'
                    }
                    ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {isProcessing && activeDropdown === action.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <action.icon className="h-4 w-4" />
                  )}
                  {action.label}
                </button>

                {/* Dropdown Options — downward, because the bar now sits at the
                    TOP of the register rather than floating at the bottom. */}
                {activeDropdown === action.id && action.options && (
                  <div
                    className="absolute left-0 top-full z-50 mt-2 min-w-[160px] rounded-lg border border-[var(--co-line)] bg-[var(--co-raised)] py-1"
                    style={{ boxShadow: 'var(--co-shadow-pop)' }}
                  >
                    {action.options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleOptionSelect(action.id, option.value)}
                        className="w-full px-4 py-2 text-left text-[12.5px] text-[var(--co-ink-2)] hover:bg-[var(--co-brass-wash)] hover:text-[var(--co-brass-ink)]"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Tag Picker Popover */}
                {activeDropdown === 'tags' && action.id === 'tags' && (
                  <div
                    className="absolute right-0 top-full z-50 mt-2 w-72 rounded-lg border border-[var(--co-line)] bg-[var(--co-raised)] p-3"
                    style={{ boxShadow: 'var(--co-shadow-pop)' }}
                  >
                    <p className="mb-2 text-[11.5px] text-[var(--co-ink-3)]">
                      Add tags to {selectedCount} ticket{selectedCount > 1 ? 's' : ''}
                    </p>
                    <TicketTagInput
                      value={tagDraft}
                      onChange={setTagDraft}
                      disabled={isProcessing}
                      placeholder="Type a tag and press Enter..."
                    />

                    {/* Already-present tags — click to add to the selection */}
                    {tagSuggestions.filter((s) => !tagDraft.includes(s.name)).length > 0 && (
                      <div className="mt-2">
                        <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 mb-1">
                          Existing tags
                        </p>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {tagSuggestions
                            .filter((s) => !tagDraft.includes(s.name))
                            .map((s) => (
                              <button
                                key={s.name}
                                type="button"
                                onClick={() => addDraftTag(s.name)}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-1 rounded-md border border-[var(--co-line-strong)] px-2 py-[3px] text-[11.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:opacity-50"
                              >
                                <Tag className="h-3 w-3" />
                                {s.name}
                                {s.count > 0 && (
                                  <span className="text-[var(--co-ink-4)]">{s.count}</span>
                                )}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="text-[11.5px] text-[var(--co-ink-4)]">
                        {tagDraft.length > 0
                          ? `${tagDraft.length} tag${tagDraft.length > 1 ? 's' : ''} ready`
                          : 'Pick or type tags'}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={closeTagPopover}
                          disabled={isProcessing}
                          className="rounded-lg px-3 py-1.5 text-[12.5px] text-[var(--co-ink-2)] hover:bg-[var(--co-brass-wash)] disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleApplyTags}
                          disabled={tagDraft.length === 0 || isProcessing}
                          className="flex items-center gap-1.5 rounded-lg bg-[var(--co-brass-ink)] px-3 py-1.5 text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
                          Add Tags
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex-1" />

          {/* Clear */}
          <button
            onClick={onClearSelection}
            aria-label="Clear selection"
            className="flex items-center gap-1.5 rounded-md px-[9px] py-[5px] text-[12.5px] text-[var(--co-ink-3)] hover:bg-[var(--co-brass-wash)] hover:text-[var(--co-ink)]"
          >
            <X className="h-[14px] w-[14px]" />
            Clear
          </button>
      </div>
    </>
  );
}

// Checkbox component for table rows
interface SelectCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
  disabled?: boolean;
}

export function SelectCheckbox({ checked, onChange, indeterminate, disabled }: SelectCheckboxProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onChange(!checked);
    }
  };

  return (
    <div
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          if (!disabled) {
            onChange(!checked);
          }
        }
      }}
      className={`
        flex h-[14px] w-[14px] items-center justify-center rounded-[3px] border transition-colors
        ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        ${checked || indeterminate
          ? 'border-[var(--co-brass-ink)] bg-[var(--co-brass-ink)]'
          : 'border-[var(--co-line-strong)] bg-[var(--co-raised)] hover:border-[var(--co-brass)]'
        }
        focus:outline-none focus:ring-1 focus:ring-[var(--co-brass)] focus:ring-offset-1
      `}
    >
      {checked && (
        <svg className="h-[10px] w-[10px] text-[var(--co-brass-on)]" viewBox="0 0 16 16" fill="none">
          <path
            d="M12 5L6.5 10.5L4 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {indeterminate && !checked && (
        <div className="h-[2px] w-2 bg-[var(--co-brass-on)]" />
      )}
    </div>
  );
}
